import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Report } from "../../pojo/Report";
import { AlertService } from "../../services/alert.service";
import { FlsService } from "../../services/fls.service";
import { ContactService } from "../../services/contact.service";
import { DocumentService } from "../../services/document.service";
import {
    DocusignApi,
    DocusignEnvelopeRow,
    DocusignRecipientRow,
    DocusignSigner,
} from "../../services/docusign.service";
import { ReportService } from "../../services/report.service";
import { TelemetryService } from "../../services/telemetry.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { AccessLevels } from "../../pojo/Fls";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { DocusignSignature } from "../system-settings/document-management/docusign.signature";
import { S25TableModule } from "../s25-table/s25.table.module";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { Table } from "../s25-table/Table";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { GenericTableListComponent } from "../s25-table/generics/generic.table.list.component";
import { S25DocusignEnvelopeFilesCellComponent } from "./s25.docusign.envelope.files.component";

@Component({
    selector: "s25-docusign-envelopes",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ButtonComponent, S25DropdownPaginatedModule, S25TableModule],
    styles: `
        .signatures {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
            margin-top: 0.5rem;
        }

        s25-ng-table {
            align-self: stretch;
            width: 100%;
        }

        .signer-form {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
        }

        s25-contact-dropdown {
            display: block;
            width: 22rem;
            max-width: 100%;
        }

        .manual-signer {
            display: flex;
            align-items: flex-end;
            gap: 1rem;
            flex-wrap: wrap;

            input {
                width: 16rem;
                max-width: 100%;
                display: block;
                margin-top: 0.35rem;
            }
        }

        .signer-row {
            display: flex;
            align-items: center;
            gap: 1rem;
        }
    `,
    template: `
        @if (canContract.value()) {
            <div class="signatures">
                <div class="ngBold">Signatures</div>
                <s25-ng-table
                    [caption]="'Signatures'"
                    [columnSortable]="true"
                    [dataSource]="tableData"
                    [hasRefresh]="true"
                    [noResultsMessage]="'No signatures yet.'"
                    [showHeaderWhenNoData]="true"
                    [unlimitedWidth]="true"
                ></s25-ng-table>

                <div class="signer-form">
                    <div>A signature line for each person is added at the end of this contract.</div>
                    <s25-contact-dropdown
                        [value]="contact()"
                        (valueChange)="contact.set($event)"
                        placeholder="Select a signer"
                    ></s25-contact-dropdown>
                    <button type="button" class="aw-button aw-button--outline" (click)="addSigner()">Add signer</button>
                    <div class="manual-signer">
                        <label>
                            Name *
                            <input
                                class="c-input"
                                [value]="manualName()"
                                (input)="manualName.set(fieldValue($event))"
                            />
                        </label>
                        <label>
                            Email *
                            <input
                                class="c-input"
                                type="email"
                                [value]="manualEmail()"
                                (input)="manualEmail.set(fieldValue($event))"
                            />
                        </label>
                        <button type="button" class="aw-button aw-button--outline" (click)="addManualSigner()">
                            Add email
                        </button>
                    </div>
                    @if (addError()) {
                        <p>{{ addError() }}</p>
                    }
                    @for (signer of signers(); track signer.email) {
                        <div class="signer-row">
                            <span>{{ signer.name }} · {{ signer.email }}</span>
                            <button
                                type="button"
                                class="aw-button aw-button--danger--outline"
                                (click)="removeSigner(signer.email)"
                            >
                                Remove
                            </button>
                        </div>
                    }
                </div>

                @if (sendError()) {
                    <p>{{ sendError() }}</p>
                }
                <s25-ng-button
                    [type]="'primary'"
                    buttonText="Send for Signature"
                    [disabled]="!canSend()"
                    [onClick]="sendForSignature"
                ></s25-ng-button>
            </div>
        }
    `,
})
export class S25DocusignEnvelopesComponent {
    readonly documentId = input.required<number>();
    readonly report = input.required<Report.SimpleObject>();
    readonly context = input.required<Report.Context>();
    readonly objectTypeId = input.required<number>();
    readonly objectId = input.required<number>();

    readonly signers = signal<DocusignSigner[]>([]);
    readonly contact = signal<DropDownItem | null>(null);
    readonly manualName = signal("");
    readonly manualEmail = signal("");
    readonly addError = signal("");
    readonly sendError = signal("");
    readonly sending = signal(false);
    readonly sendForSignature = () => this.send();
    readonly canContract = s25Resource({
        loader: async () => {
            const fls = await FlsService.getFls();
            const level = fls.CONTRACT;
            return level === AccessLevels.Read || level === AccessLevels.Edit || level === AccessLevels.Full;
        },
    });
    readonly canSend = computed(() => this.signers().length > 0);
    readonly envelopesTable = viewChild(S25TableComponent);
    readonly onVoid = (row: Table.Row) => {
        const envelope = row.data as DocusignEnvelopeRow | undefined;
        if (!envelope) return;
        return this.voidEnvelope(envelope);
    };
    readonly tableData: Table.Unpaginated = {
        type: "unpaginated",
        dataSource: () => this.loadEnvelopeRows(),
        columns: [
            { id: "status", header: "Status" },
            { id: "sentBy", header: "Sent by" },
            { id: "sent", header: "Sent" },
            {
                id: "signers",
                header: "Signers",
                sortable: false,
                cells: { component: GenericTableListComponent },
            },
            {
                id: "documents",
                header: "Documents",
                sortable: false,
                cells: { component: S25DocusignEnvelopeFilesCellComponent },
            },
            GenericTableButtonComponent.Column("Void", this.onVoid),
        ],
    };

    constructor() {
        // The table fetches when it mounts. Refresh only when the contract changes after that.
        // Do not read table.loading() here: that signal flips false when a fetch ends, and a
        // tracked read would start another fetch.
        let contractKey = "";
        effect(() => {
            if (this.canContract.value() !== true) return;
            const key = `${this.documentId()}:${this.objectTypeId()}:${this.objectId()}`;
            const previous = contractKey;
            contractKey = key;
            if (!previous || previous === key) return;
            untracked(() => {
                this.signers.set([]);
                this.contact.set(null);
                this.manualName.set("");
                this.manualEmail.set("");
                this.addError.set("");
                this.sendError.set("");
                void this.envelopesTable()?.refresh(true);
            });
        });
    }

    async addSigner() {
        this.addError.set("");
        const selected = this.contact();
        const contactId = S25Util.parseInt(selected?.itemId ?? selected?.val);
        const name = (selected?.itemName || selected?.txt || "").trim();
        if (contactId == null || !name) {
            this.addError.set("Choose a contact.");
            return;
        }
        const email = (await ContactService.getContactEmail(contactId))?.trim() ?? "";
        const error = DocusignSignature.emailError(name, email);
        if (error) {
            this.addError.set(error);
            return;
        }
        if (this.signers().some((signer) => signer.email.toLowerCase() === email.toLowerCase())) {
            this.contact.set(null);
            return;
        }
        this.signers.update((current) => [...current, { name, email }]);
        this.contact.set(null);
    }

    addManualSigner() {
        this.addError.set("");
        const name = this.manualName().trim();
        const email = this.manualEmail().trim();
        if (!name) {
            this.addError.set("Enter a name.");
            return;
        }
        const error = DocusignSignature.emailError(name, email);
        if (error) {
            this.addError.set(error);
            return;
        }
        if (this.signers().some((signer) => signer.email.toLowerCase() === email.toLowerCase())) {
            this.manualName.set("");
            this.manualEmail.set("");
            return;
        }
        this.signers.update((current) => [...current, { name, email }]);
        this.manualName.set("");
        this.manualEmail.set("");
    }

    fieldValue(event: Event): string {
        const target = event.target;
        return target instanceof HTMLInputElement ? target.value : "";
    }

    removeSigner(email: string) {
        this.signers.update((current) => current.filter((signer) => signer.email !== email));
    }

    async voidEnvelope(envelope: DocusignEnvelopeRow) {
        const confirmed = await AlertService.confirm(
            "Voiding stops signers' links. This envelope stays on the list as voided.",
        );
        if (!confirmed) return;
        const [, error] = await S25Util.Maybe(DocusignApi.voidEnvelope(envelope.docusignEnvelopeId));
        if (error) {
            this.sendError.set(S25Util.errorText(error, "DocuSign could not complete that request."));
            return;
        }
        TelemetryService.send("docusign", "void");
        this.sendError.set("");
        this.refreshEnvelopes();
    }

    async send() {
        if (this.sending() || !this.canSend()) return;

        const eventId = this.objectId();
        const objectTypeId = this.objectTypeId();
        const documentRunId = this.documentId();
        const report = { ...this.report() };
        const context = { ...this.context() };
        const reportName = report.rpt_name;
        const signers = this.signers().map((signer) => ({ name: signer.name, email: signer.email }));

        this.sending.set(true);
        this.sendError.set("");
        if (!Number.isFinite(eventId)) {
            this.sendError.set("This contract needs an event before it can be sent.");
            this.sending.set(false);
            return;
        }
        const [documentRun, runError] = await S25Util.Maybe(
            DocumentService.getEventDocumentRun(documentRunId, eventId),
        );
        if (runError || !documentRun?.id) {
            this.sendError.set(S25Util.errorText(runError, "The contract could not be prepared."));
            this.sending.set(false);
            return;
        }
        const saved = await ReportService.saveReportRun(report, {
            ...context,
            documentRunId: documentRun.id,
        });
        if (!saved || "message" in saved) {
            this.sendError.set(saved && "message" in saved ? saved.message : "The contract could not be prepared.");
            this.savingDone();
            return;
        }
        const [, error] = await S25Util.Maybe(
            DocusignApi.send({
                documentId: documentRunId,
                documentRunId: documentRun.id,
                reportId: saved.reportId,
                reportRunId: saved.reportRunId,
                objectTypeId: objectTypeId,
                objectId: eventId,
                documentName: reportName,
                recipients: signers,
            }),
        );
        this.savingDone();
        if (error) {
            this.sendError.set(S25Util.errorText(error, "DocuSign could not complete that request."));
            return;
        }
        TelemetryService.send("docusign", "send");
        this.signers.set([]);
        this.refreshEnvelopes();
    }

    private savingDone() {
        this.sending.set(false);
    }

    private refreshEnvelopes() {
        void this.envelopesTable()?.refresh(true);
    }

    private async loadEnvelopeRows(): Promise<Table.DataSourceResponse> {
        const objectTypeId = this.objectTypeId();
        const objectId = this.objectId();
        const documentId = this.documentId();
        if (!Number.isFinite(objectTypeId) || !Number.isFinite(objectId) || !Number.isFinite(documentId)) {
            return { rows: [] };
        }
        const [body, error] = await S25Util.Maybe(DocusignApi.envelopes(objectTypeId, objectId, documentId));
        const envelopes = error || !body?.envelopes ? [] : body.envelopes;
        const format = await UserprefService.getS25DateTimeformat().catch(() => "");
        return { rows: envelopes.map((envelope) => this.toRow(envelope, format ?? "")) };
    }

    private toRow(envelope: DocusignEnvelopeRow, format: string): Table.Row {
        const signers = (envelope.recipients ?? []).map((recipient) => ({ text: signerLabel(recipient, format) }));
        const canVoid = envelope.status === "sent" || envelope.status === "delivered";
        return {
            id: envelope.docusignEnvelopeId,
            name: [envelope.status, envelope.sentByUsername, ...signers.map((signer) => signer.text)]
                .filter(Boolean)
                .join(" "),
            data: envelope,
            cells: {
                status: { text: envelope.status },
                sentBy: { text: envelope.sentByUsername },
                sent: {
                    text: formatWhen(envelope.sentDt, format),
                    sortValue: envelope.sentDt ?? "",
                },
                signers: {
                    inputs: {
                        items: signers,
                        hasDecoration: false,
                        hasPagination: false,
                    },
                    textValue: signers.map((signer) => signer.text).join(" "),
                },
                documents: {
                    inputs: {
                        fileKey: envelope.fileKey ?? "",
                        sentFileName: envelope.sentFileName ?? "",
                        signedFileName: envelope.signedFileName ?? "",
                    },
                },
                ...(canVoid ? {} : { void: { if: false } }),
            },
        };
    }
}

function signerLabel(recipient: DocusignRecipientRow, format: string): string {
    const completed = formatWhen(recipient.completedDt, format);
    if (!completed) return `${recipient.name} · ${recipient.status}`;
    return `${recipient.name} · ${recipient.status} · ${completed}`;
}

function formatWhen(value: string | null | undefined, format: string): string {
    if (!value || !format) return "";
    return S25Datefilter.transform(value, format) ?? "";
}
